import { decodeHtml } from 'helpers/utils'
import { QUERY_MAPPING } from './search-native-utils'

const warnTypeError = (functionName, variableName, typeName, variable) => {
  console.warn(`Error on ${functionName}: ${variableName} must be a ${typeName}. Provided value: `, variable)
}

const getSearchOriginUrl = (params, pathname, searchType = 'product') => {
  const urlParams = `&${params.toString()}${pathname === 'search' ? `&type=${searchType}` : ''}${pathname !== 'search' || searchType === 'product' ? `&${QUERY_MAPPING.filter.code}${QUERY_MAPPING.seo.code}=0` : ''}`

  return `/${pathname}?view=ajax${urlParams}`
}

const getSortOrder = (params, sortOrder) => {
  const currentParams = new URLSearchParams(params?.toString() || '')

  if (typeof sortOrder?.direction === 'string' && sortOrder.direction.trim()) {
    currentParams.append(QUERY_MAPPING.sort.param, sortOrder.direction)
  }

  return currentParams
}

const getActiveFilters = (params, filters) => {
  let paramsFilter = params

  if (!(params instanceof URLSearchParams)) {
    warnTypeError('getFacets', 'params', 'URLSearchParams', paramsFilter)
    paramsFilter = new URLSearchParams()
  }

  Object.entries(filters).forEach(([key, values]) => {
    if (key === `${QUERY_MAPPING.filter.code}${QUERY_MAPPING.price.param}` && values?.length) {
      paramsFilter.append(`${key}${QUERY_MAPPING.separate.code}${QUERY_MAPPING.price.low}`, values[0])
      paramsFilter.append(`${key}${QUERY_MAPPING.separate.code}${QUERY_MAPPING.price.high}`, values[1])
    } else {
      values.forEach((value) => paramsFilter.append(key, decodeHtml(value.value)))
    }
  })

  return paramsFilter
}

const getPage = (params, page) => {
  params.append('page', page)

  return params
}

const getFilters = (facets, filtersConfig = []) => {
  let currentFacets = filtersConfig.length > 0 ? facets.map((facet) => ({ ...facet })) : facets

  if (filtersConfig.length > 0 && facets.length > 0) {
    const advanceFilter = []

    currentFacets.forEach((filter) => {
      const currentFilter = { ...filter }

      if (filter.field.includes('tag')) {
        filtersConfig.forEach((config) => {
          const values = filter.values.filter((value) => value.value.indexOf(config.prefix_tag) === 0)

          if (values.length > 0) {
            currentFilter.label = config.label
            currentFilter.values = values
            advanceFilter.push(currentFilter)
          }
        })
      } else {
        advanceFilter.push(currentFilter)
      }
    })

    currentFacets = advanceFilter
  }

  const sizeFields =
    currentFacets && currentFacets.length
      ? currentFacets.find((x) => x && x.field && x.field.toLowerCase().includes('size'))
      : null

  if (sizeFields) {
    sizeFields.values = sizeFields.values.sort((a, b) => {
      // a and b are SearchOrigin.Facet objects. We wish to sort by the value property on each object
      const A = a.value
      const B = b.value
      // check whether two items are the same number, but include a string - eg 7 and 7h.
      // 7H is 7 and a half, and should come after 7.

      if (parseInt(A, 10) === parseInt(B, 10) && (Number.isNaN(A) || Number.isNaN(B))) {
        return A < B ? -1 : 1
      }

      // convert to numbers so that 10 comes after 8
      return parseInt(A, 10) < parseInt(B, 10) ? -1 : 1
    })
  }

  return currentFacets
}

const getPagination = (pagination) => ({
  currentPage: pagination.currentPage,
  perPage: pagination.perPage,
  totalPages: pagination.totalPages,
  totalProducts: pagination.totalResults
})

const fetchJson = async (endpoint, config = {}, fallback = {}) => {
  try {
    const response = await fetch(endpoint, config)

    if (!response.ok) {
      const error = new Error(`HTTP error: ${response.status} - ${endpoint}`)

      error.status = response.status
      throw error
    }

    return await response.json()
  } catch (err) {
    console.warn('fetchJson error:', endpoint, 'Status:', err.status || 'Unknown', 'Error:', err)

    return fallback
  }
}

const getResponse = async (endpoint, config) => {
  if (!endpoint || typeof endpoint !== 'string') {
    warnTypeError('getResponse', 'endpoint', 'string', endpoint)
  }

  if (!config || typeof config !== 'object') {
    warnTypeError('getResponse', 'config', 'object: any', config)
  }

  const data = await fetchJson(endpoint, config, {})

  if (data?.sorting?.options?.length) {
    data.sorting.options = data.sorting.options.map((x) => ({
      ...x,
      active: x.active == null ? 0 : x.active
    }))
  }

  return data
}

const getCollectionByHandle = async (payload, collectionHandle) => {
  if (!collectionHandle || typeof collectionHandle !== 'string') {
    warnTypeError('getCollectionByHandle', 'collectionHandle', 'string', collectionHandle)
  }

  let params = new URLSearchParams()

  if (payload.sort_by) {
    params = getSortOrder(params, payload.sort_by)
  }

  if (payload.selectedValues) {
    params = getActiveFilters(params, payload.selectedValues)
  }

  if (payload.page) {
    params = getPage(params, payload.page)
  }

  const endpoint = getSearchOriginUrl(params, `collections/${collectionHandle}`)

  return getResponse(endpoint, {})
}

const getSearchResultByKeyword = async (payload, searchQuery, searchType) => {
  if (!searchQuery || typeof searchQuery !== 'string') {
    warnTypeError('getSearchResultByKeyword', 'searchQuery', 'string', searchQuery)
  }

  let params = new URLSearchParams()

  params.append('q', searchQuery)

  if (payload) {
    if (payload.originalQuery) {
      params.append('oq', payload.originalQuery)
    }

    if (payload.sort_by) {
      params = getSortOrder(params, payload.sort_by)
    }

    if (payload.selectedValues) {
      params = getActiveFilters(params, payload.selectedValues)
    }

    if (payload.page) {
      params = getPage(params, payload.page)
    }

    if (payload.tag) {
      params.append('tag', payload.tag)
    }
  }

  const endpoint = getSearchOriginUrl(params, 'search', searchType)

  return getResponse(endpoint, {})
}

const fetchSearchResults = async (payload, isSearch = false) => {
  const currentPayload = { ...payload }

  if (!currentPayload.selectedValues) {
    currentPayload.selectedValues = {}
  }

  const response = isSearch
    ? await getSearchResultByKeyword(currentPayload, currentPayload.searchQuery, currentPayload.searchType)
    : await getCollectionByHandle(currentPayload, currentPayload.handle)

  return {
    products: response.products,
    filters: getFilters(response.filters, response.filter_configs),
    sorting: response.sorting,
    pagination: getPagination(response.pagination)
  }
}

const getSuggestionSearchTerms = async (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') {
    warnTypeError('getSuggestionSearchTerms', 'searchTerm', 'string', searchTerm)
  }

  const suggestions = fetch(
    `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}
      &${encodeURIComponent('resources[type]')}=query,article,page`
  )
    .then((response) => response.json())
    .then((suggestionsData) => suggestionsData?.resources?.results)
    .catch((error) => {
      throw error
    })

  return suggestions
}

const searchResults = async (searchTerm) => {
  const term = searchTerm || null

  const payload = {}
  const [response, suggestions] = await Promise.all([
    getSearchResultByKeyword(payload, term),
    getSuggestionSearchTerms(term)
  ])

  return {
    totalResults: response.pagination.totalResults,
    products: response.products,
    queries: suggestions?.queries,
    pages: suggestions?.pages,
    articles: suggestions?.articles
  }
}


export { fetchSearchResults, searchResults }
